/**
 * @fileoverview Modal de perfil do usuário ("Meu Perfil" / Editar usuário).
 * Layout em 3 abas (Dados pessoais / Acesso / Atendimento) com header de
 * identidade (avatar + badge de perfil), rodapé fixo com indicador de
 * alterações não salvas, chips removíveis para setores e switch para o
 * "ticket sem fila". A lógica de dados (endpoints, payloads, gates de
 * permissão) é a mesma do layout anterior — só a apresentação mudou.
 */

import React, { useState, useEffect, useContext, useRef } from "react";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import {
  makeStyles,
  useTheme,
  useMediaQuery,
  Dialog,
  Button,
  TextField,
  MenuItem,
  Menu,
  Input,
  Avatar,
  Chip,
  Switch,
  IconButton,
  CircularProgress
} from "@material-ui/core";
import {
  Plus,
  Pencil,
  X,
  Camera,
  User,
  KeyRound,
  Headphones,
  EyeOff,
  Check,
  CheckCheck,
  MessageCircleMore
} from "lucide-react";
import { i18n } from "../../translate/i18n";
import UnsavedChangesHint from "../UnsavedChangesHint";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { isInternalOnly } from "../../utils/accessScope";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import { Can } from "../Can";
import rules from "../../rules";
import useWhatsApps from "../../hooks/useWhatsApps";
import ModernSelect from "../ModernSelect";
import { compressImageFile } from "../../utils/imageCompression";
import { createModalDesign } from "../../styles/modalDesign";

// Shape/valores default do formulário — contrato do payload de /users.
// allTicket: "enabled" ou "disabled" (a grafia antiga "desabled" foi corrigida
// no banco em 22/10/2026).
const DEFAULT_FORM = {
  name: "",
  email: "",
  cpf: "",
  password: "",
  profile: "user",
  whatsappId: "",
  queueIds: [],
  allTicket: "disabled",
  profileImage: "",
  isAttendant: true
};

const makeDefaultForm = () => ({ ...DEFAULT_FORM, queueIds: [] });

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    ...createModalDesign(theme),

    // Sobrepõe o mdDialog (520px): ~680px de largura e altura fixa no desktop,
    // para as abas caberem sem rolagem e o rodapé ficar sempre visível.
    userDialog: {
      "& .MuiDialog-paper": {
        width: "min(680px, calc(100vw - 32px))",
        [theme.breakpoints.up("sm")]: {
          height: "min(620px, calc(100vh - 48px))"
        }
      },
      "& .MuiDialog-paperFullScreen": {
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        borderRadius: 0
      }
    },

    // ── Header de identidade ──────────────────────────────────────────────
    userHeader: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(2, 3),
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.5, 2)
      }
    },
    avatarWrapper: {
      position: "relative",
      flexShrink: 0
    },
    headerAvatar: {
      width: 56,
      height: 56,
      fontSize: 20,
      fontWeight: 600,
      background: dark ? "rgba(91, 124, 255, 0.2)" : "rgba(61, 92, 253, 0.1)",
      color: dark ? "#7c9aff" : "#3D5CFD"
    },
    cameraBtn: {
      position: "absolute",
      bottom: -2,
      right: -2,
      width: 24,
      height: 24,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      border: `2px solid ${dark ? "#14161c" : "#ffffff"}`,
      transition: "transform 150ms ease",
      "&:hover": { transform: "scale(1.1)" }
    },
    headerIdentity: {
      flex: 1,
      minWidth: 0
    },
    headerNameRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      fontSize: "1rem",
      fontWeight: 700,
      color: dark ? "#f8fafc" : "#111827",
      lineHeight: 1.3,
      "& > span:first-child": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }
    },
    headerEmail: {
      fontSize: "0.8rem",
      color: dark ? "#94a3b8" : "#64748b",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      marginTop: 2
    },
    roleBadge: {
      flexShrink: 0,
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      lineHeight: 1.6
    },
    roleBadgeAdmin: {
      background: dark ? "rgba(91, 124, 255, 0.14)" : "rgba(61, 92, 253, 0.1)",
      color: dark ? "#7C9BFF" : "#3D5CFD",
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.3)"
        : "1px solid rgba(61, 92, 253, 0.28)"
    },
    roleBadgeUser: {
      background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.05)",
      color: dark ? "#A1A1AA" : "#64748b",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(15, 23, 42, 0.1)"
    },

    // ── Abas ──────────────────────────────────────────────────────────────
    tabsBar: {
      display: "flex",
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 3),
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.5, 2)
      }
    },
    tab: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(0.75),
      padding: "9px 12px",
      borderRadius: 12,
      fontSize: "0.85rem",
      fontWeight: 600,
      cursor: "pointer",
      color: dark ? "#94a3b8" : "#64748b",
      background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(15, 23, 42, 0.02)",
      border: "1px solid transparent",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      whiteSpace: "nowrap",
      "&:hover": {
        background: dark ? "rgba(91, 124, 255, 0.08)" : "rgba(61, 92, 253, 0.05)"
      },
      // No mobile o texto informa mais que o ícone — some o ícone pra caber.
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.75rem",
        padding: "8px 6px",
        "& svg": { display: "none" }
      }
    },
    tabActive: {
      background: dark ? "rgba(91, 124, 255, 0.15)" : "rgba(61, 92, 253, 0.08)",
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.4)"
        : "1px solid rgba(61, 92, 253, 0.28)",
      color: dark ? "#93c5fd" : "#3D5CFD"
    },

    // ── Conteúdo ──────────────────────────────────────────────────────────
    userContent: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto", // válvula: as abas são projetadas pra caber sem rolar
      padding: theme.spacing(2.5, 3),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(2)
      }
    },

    // ── Chips de setores ──────────────────────────────────────────────────
    chipsWrap: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(0.75)
    },
    queueChip: {
      borderRadius: 999,
      background: dark ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.05)",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(15, 23, 42, 0.1)",
      color: dark ? "#e6e8eb" : "#1e293b",
      fontWeight: 500,
      "& .MuiChip-deleteIcon": {
        color: dark ? "rgba(255, 255, 255, 0.4)" : "rgba(15, 23, 42, 0.35)",
        "&:hover": { color: dark ? "#f8fafc" : "#0f172a" }
      }
    },
    chipDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      marginLeft: 8,
      flexShrink: 0
    },
    noQueuesText: {
      fontSize: "0.8rem",
      color: dark ? "#71717A" : "#94a3b8"
    },
    addQueueBtn: {
      height: 28,
      padding: "0 10px",
      borderRadius: 999,
      fontSize: "0.78rem",
      fontWeight: 600,
      textTransform: "none",
      color: dark ? "#7c9aff" : "#3D5CFD",
      border: dark
        ? "1px dashed rgba(91, 124, 255, 0.4)"
        : "1px dashed rgba(61, 92, 253, 0.35)",
      background: "transparent",
      "&:hover": {
        background: dark ? "rgba(91, 124, 255, 0.08)" : "rgba(61, 92, 253, 0.05)"
      }
    },
    queueMenuPaper: {
      borderRadius: 14,
      marginTop: 4,
      background: dark ? "#1c1f26" : "#ffffff",
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.2)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow: dark
        ? "0 12px 32px rgba(0, 0, 0, 0.45)"
        : "0 12px 32px rgba(15, 23, 42, 0.12)"
    },
    queueMenuList: {
      padding: 6,
      "& .MuiMenuItem-root": {
        borderRadius: 10,
        fontSize: "0.85rem",
        display: "flex",
        alignItems: "center",
        gap: 8,
        "&:hover": {
          background: dark
            ? "rgba(91, 124, 255, 0.1)"
            : "rgba(61, 92, 253, 0.06)"
        }
      }
    },
    queueMenuItemSelected: {
      background: dark ? "rgba(91, 124, 255, 0.12)" : "rgba(61, 92, 253, 0.07)"
    },
    queueMenuItemLabel: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    queueMenuCheck: {
      color: dark ? "#7c9aff" : "#3D5CFD",
      flexShrink: 0
    },
    queueMenuAllItem: {
      fontWeight: 600,
      color: dark ? "#7c9aff" : "#3D5CFD",
      marginBottom: 4,
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.06)",
      borderRadius: "10px 10px 0 0 !important",
      paddingBottom: 10
    },

    // ── Switch "ticket sem fila" ──────────────────────────────────────────
    switchCard: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      borderRadius: 12,
      background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(15, 23, 42, 0.08)"
    },
    switchIconBox: {
      width: 36,
      height: 36,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(91, 124, 255, 0.12)" : "rgba(61, 92, 253, 0.08)",
      color: dark ? "#7c9aff" : "#3D5CFD"
    },
    switchTexts: {
      flex: 1,
      minWidth: 0
    },
    switchTitle: {
      fontSize: "0.9rem",
      fontWeight: 600,
      color: dark ? "#FAFAFA" : "#1e293b",
      lineHeight: 1.3
    },
    switchDesc: {
      fontSize: "0.78rem",
      color: dark ? "#71717A" : "#94a3b8",
      lineHeight: 1.4,
      marginTop: 2
    },
    switchControl: {
      "& .MuiSwitch-switchBase.Mui-checked": { color: "#5B7CFF" },
      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#5B7CFF",
        opacity: 0.6
      }
    },

    // ── Rodapé fixo ───────────────────────────────────────────────────────
    userFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.75, 3),
      borderTop: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      background: dark ? "rgba(0, 0, 0, 0.22)" : "rgba(15, 23, 42, 0.02)",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.5, 2)
      }
    },
    footerDirty: {
      display: "flex",
      alignItems: "center",
      minWidth: 0,
      overflow: "hidden"
    },
    footerActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexShrink: 0
    },
    btnBase: {
      height: 38,
      padding: "0 16px",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      whiteSpace: "nowrap",
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "none",
      boxShadow: "none",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      "& svg": { flexShrink: 0 },
      "& .MuiButton-label": { whiteSpace: "nowrap" }
    },
    btnCancel: {
      background: "transparent",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
      "&:hover": {
        background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
        color: dark ? "#f8fafc" : "#111827"
      }
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "&:hover": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)"
      },
      "&:disabled": { opacity: 0.55, cursor: "not-allowed" }
    },
    spinner: { color: "#ffffff" }
  };
});

const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user: loggedInUser } = useContext(AuthContext);
  const loggedInUserId = loggedInUser?.id;
  const { whatsApps } = useWhatsApps();

  const [formData, setFormData] = useState(makeDefaultForm);

  const [imagePreview, setImagePreview] = useState("");
  const [readOnlyJobRole, setReadOnlyJobRole] = useState("");
  const [queues, setQueues] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [userCompanyIds, setUserCompanyIds] = useState([]);
  const [userMaxCompanies, setUserMaxCompanies] = useState(1);
  const [activeTab, setActiveTab] = useState("personal");
  const [queueMenuAnchor, setQueueMenuAnchor] = useState(null);

  // Snapshot do formData carregado — base do indicador "alterações não salvas".
  // Máx. empresas / Empresas do Grupo ficam fora (auto-salvam no onChange).
  const initialFormRef = useRef(JSON.stringify(DEFAULT_FORM));

  // Mesmos checks do <Can> (rules.js), em forma imperativa, para decidir
  // visibilidade de abas e arranjo da grade. Os <Can> dos campos continuam.
  const canPerform = (action) =>
    !!rules[loggedInUser.profile]?.static?.includes(action);
  // Com a regra nova, como a API (UserServices/userActions): sem team.manage, a
  // pessoa só muda os próprios dados pessoais; o perfil pede team.manage_access e
  // as empresas do grupo, team.manage_companies.
  const { can } = usePermission();
  const canEditProfile = can("team.manage", canPerform("user-modal:editProfile"));
  const showMaxCompanies = !!userId && !!loggedInUser.super;
  const showGroupCompanies =
    !!userId &&
    availableCompanies.length > 0 &&
    can("team.manage_companies", isAdminUser(loggedInUser));

  useEffect(() => {
    const fetchQueues = async () => {
      // Escopo "Apenas Interno" recebe 403 em /queue. Este modal fica montado
      // no layout mesmo fechado, então sem esta guarda o toast apareceria em
      // toda navegação.
      if (isInternalOnly()) return;
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchQueues();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        const fresh = makeDefaultForm();
        setFormData(fresh);
        initialFormRef.current = JSON.stringify(fresh);
        setImagePreview("");
        setReadOnlyJobRole("");
        return;
      }

      try {
        const { data } = await api.get(`/users/${userId}`);
        // "digitando…" no WhatsApp do cliente: preferência do próprio usuário
        // (uiPreferences, padrão desligado), lida em /auth/me.
        const self = Number(userId) === Number(loggedInUserId);
        const prefs = self ? (await api.get("/auth/me").catch(() => ({ data: {} }))).data?.uiPreferences || {} : null;
        const loaded = {
          name: data.name || "",
          email: data.email || "",
          cpf: data.cpf || "",
          password: "",
          profile: data.profile || "user",
          whatsappId: data.whatsappId || "",
          queueIds: data.queues?.map(q => q.id) || [],
          allTicket: data.allTicket || "disabled",
          profileImage: data.profileImage || "",
          isAttendant: data.isAttendant !== undefined ? data.isAttendant : true,
          ...(prefs ? { showTypingToCustomer: prefs.showTypingToCustomer === true } : {})
        };
        setFormData(loaded);
        initialFormRef.current = JSON.stringify(loaded);
        setImagePreview(data.profileImage || "");
        setReadOnlyJobRole(data.jobRole?.name || i18n.t("userModal.form.noJobRole"));
        setUserMaxCompanies(data.maxCompanies || 1);

        // Fetch companies do user e disponíveis
        try {
          const [companiesRes, availableRes] = await Promise.all([
            api.get(`/users/${userId}/companies`),
            api.get(`/users/${userId}/available-companies`)
          ]);
          setUserCompanyIds((companiesRes.data || []).map(c => c.id));
          setAvailableCompanies(availableRes.data || []);
        } catch { setAvailableCompanies([]); setUserCompanyIds([]); }
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchUser();
    }
  }, [userId, open, loggedInUserId]);

  const isDirty = JSON.stringify(formData) !== initialFormRef.current;
  // Preferências próprias (Meu perfil): só quando o modal é o do usuário logado.
  const isSelf = !!userId && Number(userId) === Number(loggedInUserId);

  const handleClose = () => {
    const fresh = makeDefaultForm();
    setFormData(fresh);
    initialFormRef.current = JSON.stringify(fresh);
    setImagePreview("");
    setReadOnlyJobRole("");
    setIsSubmitting(false);
    setActiveTab("personal");
    setQueueMenuAnchor(null);
    onClose();
  };

  // Comprime antes de virar base64: o avatar e gravado inline na coluna TEXT
  // Users.profileImage, e o arquivo cru chegava a MBs por usuario, inchando
  // toda query que le a tabela. 200x200 basta pro tamanho exibido.
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const compressed = await compressImageFile(file, {
        maxWidth: 200,
        maxHeight: 200,
        quality: 0.8
      });
      setImagePreview(compressed);
      setFormData({ ...formData, profileImage: compressed });
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveUser = async () => {
    setIsSubmitting(true);
    try {
      // A preferência do "digitando…" vai à parte (PATCH das preferências do
      // próprio usuário); só ela mudou, o usuário não é regravado.
      const { showTypingToCustomer, ...userFields } = formData;
      const { showTypingToCustomer: initialTyping, ...initialUserFields } = JSON.parse(
        initialFormRef.current || "{}"
      );
      const userChanged = !userId || JSON.stringify(userFields) !== JSON.stringify(initialUserFields);
      const userData = {
        ...userFields,
        profileImage: imagePreview
      };

      if (!userId) {
        await api.post("/users", userData);
      } else if (userChanged) {
        await api.put(`/users/${userId}`, userData);
      }

      if (isSelf && showTypingToCustomer !== undefined && showTypingToCustomer !== initialTyping) {
        await api.patch("/auth/me/ui-preferences", { showTypingToCustomer });
      }

      toast.success(i18n.t("userModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Abas visíveis por perfil (decisão: ocultar abas sem nenhum campo). Com os
  // gates atuais nenhuma fica vazia (Senha e Tipo operacional não têm gate),
  // mas o filtro protege contra gates futuros.
  const visibleTabs = [
    { id: "personal", icon: User, label: i18n.t("userModal.tabs.personal"), visible: true },
    { id: "access", icon: KeyRound, label: i18n.t("userModal.tabs.access"), visible: true },
    { id: "service", icon: Headphones, label: i18n.t("userModal.tabs.service"), visible: true }
  ].filter((t) => t.visible);

  const safeActiveTab = visibleTabs.some((t) => t.id === activeTab)
    ? activeTab
    : visibleTabs[0].id;

  const optional = i18n.t("userModal.form.optionalSuffix");
  const isAdminProfile = formData.profile === "admin";
  const selectedQueues = formData.queueIds.map((id) => ({
    id,
    queue: queues.find((q) => q.id === id)
  }));
  const allQueuesSelected =
    queues.length > 0 && queues.every((q) => formData.queueIds.includes(q.id));

  // Toggle com functional update: o menu fica aberto para seleção múltipla,
  // então cliques rápidos não podem depender de um formData de closure stale.
  const toggleQueueId = (id) => {
    setFormData((prev) => ({
      ...prev,
      queueIds: prev.queueIds.includes(id)
        ? prev.queueIds.filter((x) => x !== id)
        : [...prev.queueIds, id]
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={`${classes.mdDialog} ${classes.userDialog}`}
      maxWidth={false}
      fullScreen={isMobile}
    >
      {/* ── Header: identidade de quem está sendo editado ── */}
      <div className={classes.userHeader}>
        <div className={classes.avatarWrapper}>
          <Avatar
            src={imagePreview || formData.profileImage || undefined}
            className={classes.headerAvatar}
          >
            {(formData.name || "?").charAt(0).toUpperCase()}
          </Avatar>
          <Input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: "none" }}
            id="profile-image-upload"
          />
          <label
            htmlFor="profile-image-upload"
            className={classes.cameraBtn}
            title={i18n.t("userModal.form.changePhoto")}
            aria-label={i18n.t("userModal.form.changePhoto")}
          >
            <Camera size={12} strokeWidth={2} />
          </label>
        </div>
        <div className={classes.headerIdentity}>
          <div className={classes.headerNameRow}>
            <span>
              {formData.name ||
                (userId
                  ? i18n.t("userModal.title.edit")
                  : i18n.t("userModal.title.add"))}
            </span>
            <span
              className={`${classes.roleBadge} ${
                isAdminProfile ? classes.roleBadgeAdmin : classes.roleBadgeUser
              }`}
            >
              {i18n.t(isAdminProfile ? "users.table.admin" : "users.table.user")}
            </span>
          </div>
          {formData.email && (
            <div className={classes.headerEmail}>{formData.email}</div>
          )}
        </div>
        <IconButton size="small" onClick={handleClose} className={classes.mdCloseBtn}>
          <X size={18} strokeWidth={1.75} />
        </IconButton>
      </div>

      {/* ── Abas ── */}
      <div className={classes.tabsBar}>
        {visibleTabs.map((t) => {
          const TabIcon = t.icon;
          return (
            <div
              key={t.id}
              className={`${classes.tab} ${
                safeActiveTab === t.id ? classes.tabActive : ""
              }`}
              onClick={() => setActiveTab(t.id)}
              role="tab"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setActiveTab(t.id)}
            >
              <TabIcon size={16} strokeWidth={1.8} />
              {t.label}
            </div>
          );
        })}
      </div>

      {/* ── Conteúdo da aba ativa ── */}
      <div className={classes.userContent}>
        {safeActiveTab === "personal" && (
          <>
            {userId ? (
              <div className={classes.mdFieldRow}>
                <TextField
                  label={i18n.t("userModal.form.name")}
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  fullWidth
                  className={classes.mdField}
                />
                <TextField
                  label={`${i18n.t("userModal.form.jobRole")} ${optional}`}
                  variant="outlined"
                  value={readOnlyJobRole}
                  fullWidth
                  disabled
                  className={classes.mdField}
                />
              </div>
            ) : (
              <TextField
                label={i18n.t("userModal.form.name")}
                variant="outlined"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                fullWidth
                className={classes.mdField}
              />
            )}

            <TextField
              label={i18n.t("userModal.form.email")}
              variant="outlined"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              fullWidth
              className={classes.mdField}
            />

            <div className={classes.mdFieldRow}>
              <InputMask
                mask="999.999.999-99"
                maskChar={null}
                value={formData.cpf}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label={`${i18n.t("userModal.form.cpf")} ${optional}`}
                    variant="outlined"
                    fullWidth
                    className={classes.mdField}
                  />
                )}
              </InputMask>
            </div>
          </>
        )}

        {safeActiveTab === "access" && (
          <>
            {canEditProfile ? (
              <div className={classes.mdFieldRow}>
                <Can
                  role={loggedInUser.profile}
                  perform="user-modal:editProfile"
                  permission="team.manage_access"
                  yes={() => (
                    <ModernSelect
                      label={i18n.t("userModal.form.profile")}
                      value={formData.profile}
                      onChange={(e) =>
                        setFormData({ ...formData, profile: e.target.value })
                      }
                      formControlProps={{ className: classes.mdField }}
                    >
                      <MenuItem value="admin">{i18n.t("users.table.admin")}</MenuItem>
                      <MenuItem value="user">{i18n.t("users.table.user")}</MenuItem>
                    </ModernSelect>
                  )}
                />
                <TextField
                  label={i18n.t("userModal.form.password")}
                  variant="outlined"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  fullWidth
                  className={classes.mdField}
                  placeholder={userId ? i18n.t("userModal.form.passwordKeepHint") : ""}
                />
              </div>
            ) : (
              <TextField
                label={i18n.t("userModal.form.password")}
                variant="outlined"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                fullWidth
                className={classes.mdField}
                placeholder={userId ? i18n.t("userModal.form.passwordKeepHint") : ""}
              />
            )}
          </>
        )}

        {safeActiveTab === "service" && (
          <>
            {canEditProfile ? (
              <div className={classes.mdFieldRow}>
                <ModernSelect
                  label={i18n.t("userModal.form.whatsapp")}
                  value={formData.whatsappId}
                  onChange={(e) =>
                    setFormData({ ...formData, whatsappId: e.target.value })
                  }
                  formControlProps={{ className: classes.mdField }}
                >
                  <MenuItem value="">{i18n.t("userModal.form.noWhatsapp")}</MenuItem>
                  {whatsApps.length === 0 ? (
                    <MenuItem value="" disabled>
                      {i18n.t("userModal.form.noConnectionAvailable")}
                    </MenuItem>
                  ) : (
                    whatsApps.map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name}
                      </MenuItem>
                    ))
                  )}
                </ModernSelect>
                <ModernSelect
                  label={i18n.t("userModal.form.operationalType")}
                  value={formData.isAttendant ? "atendente" : "supervisor"}
                  onChange={(e) =>
                    setFormData({ ...formData, isAttendant: e.target.value === "atendente" })
                  }
                  formControlProps={{ className: classes.mdField }}
                >
                  <MenuItem value="atendente">{i18n.t("userModal.form.attendant")}</MenuItem>
                  <MenuItem value="supervisor">{i18n.t("userModal.form.supervisor")}</MenuItem>
                </ModernSelect>
              </div>
            ) : (
              <ModernSelect
                label={i18n.t("userModal.form.operationalType")}
                value={formData.isAttendant ? "atendente" : "supervisor"}
                onChange={(e) =>
                  setFormData({ ...formData, isAttendant: e.target.value === "atendente" })
                }
                formControlProps={{ className: classes.mdField }}
              >
                <MenuItem value="atendente">{i18n.t("userModal.form.attendant")}</MenuItem>
                <MenuItem value="supervisor">{i18n.t("userModal.form.supervisor")}</MenuItem>
              </ModernSelect>
            )}

            <Can
              role={loggedInUser.profile}
              perform="user-modal:editQueues"
              permission="team.manage"
              yes={() => (
                <div>
                  <span className={classes.mdFieldLabel}>
                    {i18n.t("userModal.form.queues")}
                  </span>
                  <div className={classes.chipsWrap}>
                    {selectedQueues.length === 0 && (
                      <span className={classes.noQueuesText}>
                        {i18n.t("userModal.form.noQueuesSelected")}
                      </span>
                    )}
                    {selectedQueues.map(({ id, queue }) => (
                      <Chip
                        key={id}
                        size="small"
                        className={classes.queueChip}
                        icon={
                          <span
                            className={classes.chipDot}
                            style={{ backgroundColor: queue?.color || "#94a3b8" }}
                          />
                        }
                        label={queue?.name || `#${id}`}
                        onDelete={() =>
                          setFormData({
                            ...formData,
                            queueIds: formData.queueIds.filter((x) => x !== id)
                          })
                        }
                      />
                    ))}
                    <Button
                      className={classes.addQueueBtn}
                      onClick={(e) => setQueueMenuAnchor(e.currentTarget)}
                      startIcon={<Plus size={14} strokeWidth={2} />}
                    >
                      {i18n.t("userModal.form.addQueue")}
                    </Button>
                  </div>
                  {/* Menu multi-seleção: clicar alterna o setor SEM fechar
                      (fecha só fora/Esc), permitindo marcar vários de uma vez. */}
                  <Menu
                    anchorEl={queueMenuAnchor}
                    open={Boolean(queueMenuAnchor)}
                    onClose={() => setQueueMenuAnchor(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    classes={{ paper: classes.queueMenuPaper, list: classes.queueMenuList }}
                  >
                    {queues.length === 0 ? (
                      <MenuItem disabled>
                        {i18n.t("userModal.form.noQueueAvailable")}
                      </MenuItem>
                    ) : (
                      [
                        <MenuItem
                          key="__all__"
                          className={classes.queueMenuAllItem}
                          disabled={allQueuesSelected}
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              queueIds: queues.map((q) => q.id)
                            }))
                          }
                        >
                          <CheckCheck size={15} strokeWidth={2} />
                          {i18n.t("userModal.form.addAllQueues")}
                        </MenuItem>,
                        ...queues.map((q) => {
                          const isSelected = formData.queueIds.includes(q.id);
                          return (
                            <MenuItem
                              key={q.id}
                              className={isSelected ? classes.queueMenuItemSelected : ""}
                              onClick={() => toggleQueueId(q.id)}
                            >
                              <span
                                className={classes.chipDot}
                                style={{ backgroundColor: q.color || "#94a3b8", marginLeft: 0 }}
                              />
                              <span className={classes.queueMenuItemLabel}>{q.name}</span>
                              {isSelected && (
                                <Check
                                  size={14}
                                  strokeWidth={2.5}
                                  className={classes.queueMenuCheck}
                                />
                              )}
                            </MenuItem>
                          );
                        })
                      ]
                    )}
                  </Menu>
                </div>
              )}
            />

            <Can
              role={loggedInUser.profile}
              perform="user-modal:editProfile"
              permission="team.manage"
              yes={() => (
                <div className={classes.switchCard}>
                  <div className={classes.switchIconBox}>
                    <EyeOff size={18} strokeWidth={1.8} />
                  </div>
                  <div className={classes.switchTexts}>
                    <div className={classes.switchTitle}>
                      {i18n.t("userModal.form.allTicketSwitchTitle")}
                    </div>
                    <div className={classes.switchDesc}>
                      {i18n.t("userModal.form.allTicketSwitchDescription")}
                    </div>
                  </div>
                  <Switch
                    size="small"
                    className={classes.switchControl}
                    checked={formData.allTicket === "enabled"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        allTicket: e.target.checked ? "enabled" : "disabled"
                      })
                    }
                  />
                </div>
              )}
            />

            {isSelf && formData.showTypingToCustomer !== undefined && (
              <div className={classes.switchCard}>
                <div className={classes.switchIconBox}>
                  <MessageCircleMore size={18} strokeWidth={1.8} />
                </div>
                <div className={classes.switchTexts}>
                  <div className={classes.switchTitle}>
                    {i18n.t("userModal.form.typingSwitchTitle")}
                  </div>
                  <div className={classes.switchDesc}>
                    {i18n.t("userModal.form.typingSwitchDescription")}
                  </div>
                </div>
                <Switch
                  size="small"
                  className={classes.switchControl}
                  checked={!!formData.showTypingToCustomer}
                  onChange={(e) =>
                    setFormData({ ...formData, showTypingToCustomer: e.target.checked })
                  }
                  inputProps={{ "aria-label": i18n.t("userModal.form.typingSwitchTitle") }}
                />
              </div>
            )}

            {(showMaxCompanies || showGroupCompanies) && (
              <div className={classes.mdFieldRow}>
                {showMaxCompanies && (
                  <TextField
                    label={i18n.t("userModal.form.maxCompanies")}
                    type="number"
                    variant="outlined"
                    inputProps={{ min: 1, max: 50 }}
                    value={userMaxCompanies}
                    onChange={(e) => {
                      const val = Math.max(1, parseInt(e.target.value) || 1);
                      setUserMaxCompanies(val);
                      api.put(`/users/${userId}`, { maxCompanies: val }).catch(() => {});
                    }}
                    helperText={i18n.t("userModal.form.maxCompaniesHelper")}
                    className={classes.mdField}
                  />
                )}
                {showGroupCompanies && (
                  <ModernSelect
                    label={i18n.t("userModal.form.groupCompanies")}
                    multiple
                    value={userCompanyIds}
                    onChange={async (e) => {
                      const newIds = e.target.value;
                      const added = newIds.filter(id => !userCompanyIds.includes(id));
                      const removed = userCompanyIds.filter(id => !newIds.includes(id));
                      try {
                        await Promise.all([
                          ...added.map(cid =>
                            api.post(`/users/${userId}/companies`, { companyId: cid })
                          ),
                          ...removed.map(cid =>
                            api.delete(`/users/${userId}/companies/${cid}`)
                          ),
                        ]);
                        setUserCompanyIds(newIds);
                      } catch (err) {
                        toastError(err);
                        const { data } = await api.get(`/users/${userId}/companies`);
                        setUserCompanyIds((data || []).map(c => c.id));
                      }
                    }}
                    renderValue={(selected) =>
                      availableCompanies
                        .filter(c => selected.includes(c.id))
                        .map(c => c.name)
                        .join(", ")
                    }
                    formControlProps={{ className: classes.mdField }}
                  >
                    {availableCompanies.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </ModernSelect>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Rodapé fixo ── */}
      <div className={classes.userFooter}>
        <div className={classes.footerDirty}>
          <UnsavedChangesHint show={isDirty} />
        </div>
        <div className={classes.footerActions}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            className={`${classes.btnBase} ${classes.btnCancel}`}
            startIcon={<X size={16} strokeWidth={1.75} />}
          >
            {i18n.t("userModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSaveUser}
            disabled={isSubmitting || (!!userId && !isDirty)}
            className={`${classes.btnBase} ${classes.btnPrimary}`}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={14} className={classes.spinner} />
              ) : userId ? (
                <Pencil size={16} strokeWidth={1.75} />
              ) : (
                <Plus size={16} strokeWidth={1.75} />
              )
            }
          >
            {userId
              ? i18n.t("userModal.buttons.saveChanges")
              : i18n.t("userModal.buttons.okAdd")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default UserModal;
